import React, {useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Modal} from 'antd';
import {CheckCircleTwoTone, UserAddOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';

import PageTitle from '../../../components/page/PageTitle';
import {
    accountCreated,
    accountCreatedAndLoaded,
    accountCreatedButNotLoaded,
    accountCreationInstruction,
} from '../../../helpers/text';
import StepsWrapper from '../../../components/StepsWrapper';
import AccountCreateTypes from '../../../components/account/AccountCreateTypes';
import AccountCreateInformation from '../../../components/account/AccountCreateInformation';
import AccountCreateReview from '../../../components/account/AccountCreateReview';
import {messageType, showMessages} from '../../../helpers/message';
import {createAccount, syncRolesToProfile} from '../../../helpers/account';
import {getAccountMembershipNumber} from '../../../lib/activeMember';
import {useLazyGetProfileQuery} from '../../../store/api/accountApi';
import {usePutInJailMutation} from '../../../store/api/jailApi';

import './AccountCreate.less';

const AccountCreate = () => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);
    const navigate = useNavigate();

    const [loadActiveMember] = useLazyGetProfileQuery();
    const [putInJail, {error: jailError}] = usePutInJailMutation();

    if (jailError) {
        // Update to full RTK pattern when refactored to TSX.
        showMessages(jailError.data.message, messageType.error);
    }

    // Meta states
    const [loading, setLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);

    // Account type states
    const [selectPersonal, setSelectPersonal] = useState(true);
    const [selectBusiness, setSelectBusiness] = useState(false);

    // Pre-existing person states
    const [personID, setPersonID] = useState(null);

    // Personal account data states
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [birthDate, setBirthDate] = useState(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // Business account data states
    const [businessName, setBusinessName] = useState('');
    const [nameOnContract, setNameOnContract] = useState('');
    const [dbaName, setDBAName] = useState('');
    const [websiteAddress, setWebsiteAddress] = useState('');
    const [taxRegistrationNumber, setTaxRegistrationNumber] = useState('');
    const [businessRegistrationNumber, setBusinessRegistrationNumber] = useState('');
    const [industryCode, setIndustryCode] = useState('');
    const [companySize, setCompanySize] = useState('');

    // Form submission states
    const [checked, setChecked] = useState(false);
    const [showCheckBoxError, setShowCheckBoxError] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const goNext = () => {
        setCurrentStep(currentStep + 1);
    }

    const goBack = (e) => {
        e.preventDefault();
        // If a user selects pre-existing but then goes back and
        // navigates to create a new member, clear all the information fields.
        if (personID !== null) {
            setPersonID(null);
            setFirstName(null);
            setLastName(null);
            setEmail(null);
            setBirthDate(null);
        }
        setChecked(false);
        setShowCheckBoxError(false);
        setCurrentStep(currentStep - 1);
    }

    const handleFinish = async () => {
        if (!checked) {
            setShowCheckBoxError(true);
        } else {
            setShowCheckBoxError(false);

            setLoading(true);
            const abortSignal = abortSignalRef.current;

            const formattedBirthDate = birthDate ? dayjs(birthDate).format('YYYY-MM-DD') : null;

            try {
                const profileResponse = await createAccount({
                    firstName,
                    lastName,
                    birthDate: formattedBirthDate,
                    email,
                    password,
                    personID,
                    businessName,
                    nameOnContract,
                    dbaName,
                    websiteAddress,
                    taxRegistrationNumber,
                    businessRegistrationNumber,
                    industryCode,
                    companySize
                },  abortSignal);

                if (abortSignal.aborted === false) {
                    const profileID = profileResponse.id;
                    await loadActiveMember(profileID, abortSignal);

                    const newProfileRoles = [
                        'user.regular',
                        'user.driver',
                        'user.owner',
                        'user.booker',
                    ];

                    // Gives the newly created account/profile the basic roles.
                    await syncRolesToProfile(profileID, null, newProfileRoles, abortSignal);

                    // Lazy RTK-Q that sets new user profile into state.
                    await loadActiveMember(profileID);

                    // Put profile into pending approval jail.
                    await putInJail({
                        modelID: profileID,
                        modelType: 'profile',
                        jailName: 'pending approval',
                        startedAt: dayjs().format('YYYY-MM-DD HH:mm:ssZZ'),
                        reason: 'Registration complete, profile pending approval.',
                    });

                    setShowSuccessModal(true);
                }
                setLoading(false);
            } catch (error) {
                setLoading(false);
                showMessages(error.message, messageType.error);
            }
        }
    }

    const navigateToRestrictionPage = () => {
        const accountMemberNumber = getAccountMembershipNumber();
        if (!accountMemberNumber) {
            showMessages(accountCreatedButNotLoaded, messageType.error);
        }
        if (!personID) {
            showMessages(accountCreatedAndLoaded, messageType.success);
        } else {
            showMessages(accountCreated, messageType.success);
        }
        navigate('/account/account_info', {state: {activeKey: 3}})
    }

    const steps = [
        {
            title: 'Select Account Type',
            content:
                <AccountCreateTypes
                    selectPersonal={selectPersonal}
                    setSelectPersonal={setSelectPersonal}
                    selectBusiness={selectBusiness}
                    setSelectBusiness={setSelectBusiness}
                    handleFinish={goNext}
                />,
            form: 'account-create-types'
        },
        {
            title: 'Fill in Information',
            content:
                <AccountCreateInformation
                    isBusiness={selectBusiness}
                    firstName={firstName}
                    setFirstName={setFirstName}
                    lastName={lastName}
                    setLastName={setLastName}
                    birthDate={birthDate}
                    setBirthDate={setBirthDate}
                    email={email}
                    setEmail={setEmail}
                    password={password}
                    setPassword={setPassword}
                    confirmPassword={confirmPassword}
                    setConfirmPassword={setConfirmPassword}
                    personID={personID}
                    setPersonID={setPersonID}
                    businessName={businessName}
                    setBusinessName={setBusinessName}
                    nameOnContract={nameOnContract}
                    setNameOnContract={setNameOnContract}
                    dbaName={dbaName}
                    setDBAName={setDBAName}
                    websiteAddress={websiteAddress}
                    setWebsiteAddress={setWebsiteAddress}
                    taxRegistrationNumber={taxRegistrationNumber}
                    setTaxRegistrationNumber={setTaxRegistrationNumber}
                    businessRegistrationNumber={businessRegistrationNumber}
                    setBusinessRegistrationNumber={setBusinessRegistrationNumber}
                    industryCode={industryCode}
                    setIndustryCode={setIndustryCode}
                    companySize={companySize}
                    setCompanySize={setCompanySize}
                    handleFinish={goNext}
                />,
            form: 'account-create-information'
        },
        {
            title: 'Review',
            content:
                <AccountCreateReview
                    isBusiness={selectBusiness}
                    firstName={firstName}
                    lastName={lastName}
                    birthDate={birthDate}
                    email={email}
                    businessName={businessName}
                    nameOnContract={nameOnContract}
                    dbaName={dbaName}
                    websiteAddress={websiteAddress}
                    taxRegistrationNumber={taxRegistrationNumber}
                    businessRegistrationNumber={businessRegistrationNumber}
                    industryCode={industryCode}
                    companySize={companySize}
                    checked={checked}
                    setChecked={setChecked}
                    showCheckBoxError={showCheckBoxError}
                />
        }
    ];

    return (
        <>
            <PageTitle title="Create New Account" icon={<UserAddOutlined/>}/>
            <p>{accountCreationInstruction}</p>
            <StepsWrapper
                currentStep={currentStep}
                steps={steps}
                goBack={goBack}
                isLoading={loading}
                submitText="Create Account"
                handleFinish={handleFinish}
            />
            <Modal
                title={<><CheckCircleTwoTone twoToneColor="#52c41a" />Success</>}
                open={showSuccessModal}
                onOk={navigateToRestrictionPage}
                okButtonProps={{type: 'primary'}}
                okText="OK"
                cancelButtonProps={{ style: { display: 'none' } }}
                closable={false}
            >
                <p>{accountCreated}</p>
            </Modal>
        </>
    );
}

AccountCreate.propTypes = {};

export default AccountCreate;
